import { z } from 'zod/v4'

import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Correo › Proveedor»: the platform e-mail provider, what the organizations without their own get and who uses it,
 * in one answer. The schemas mirror `EmailSettings` and `PlatformEmailSettingsView` of
 * `contracts/admin/email.types.ts` (the type assertions of `shared-email-settings-screen.type-assertions.ts` tie
 * them in both directions). The API key NEVER travels: only `hasApiKey` and `apiKeyLast4`. Dates are ISO text.
 */

const emailProviderNameSchema = z.enum(['capture', 'console', 'resend', 'sendgrid', 'ses', 'smtp'])

export const adminEmailSettingsSchema = z.strictObject({
  id: z.string().optional(),
  provider: emailProviderNameSchema,
  fromEmail: z.string().optional(),
  fromName: z.string().optional(),
  replyToEmail: z.string().optional(),
  isActive: z.boolean(),
  hasApiKey: z.boolean().optional(),
  apiKeyLast4: z.string().optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.strictObject({ id: z.string(), email: z.string() }).nullable().optional(),
  lastChangedFields: z.array(z.string()).optional(),
  lastTestedAt: z.string().nullable().optional(),
  lastTestResult: z
    .strictObject({
      success: z.boolean(),
      message: z.string().optional(),
      timestamp: z.string(),
      outcome: z.enum(['sent', 'console', 'failed']).optional(),
      recipient: z.string().optional(),
      providerMessageId: z.string().optional(),
      logId: z.string().optional(),
      durationMs: z.number().optional(),
    })
    .nullable()
    .optional(),
})

export const adminPlatformEmailSettingsViewSchema = z.strictObject({
  configured: z.boolean(),
  settings: adminEmailSettingsSchema.nullable(),
  defaults: z.strictObject({ fromEmail: z.string(), fromName: z.string() }),
  effective: z.strictObject({
    source: z.enum(['tenant', 'platform', 'console_fallback', 'none']),
    provider: emailProviderNameSchema.nullable(),
    reason: z.enum(['not_configured', 'platform_disabled', 'console_in_production']).optional(),
  }),
  scope: z.strictObject({
    organizations: z.number(),
    inheriting: z.number(),
    ownProvider: z.number(),
    ownProviderOrganizations: z.array(z.strictObject({ id: z.string(), name: z.string() })),
  }),
  environment: z.strictObject({
    production: z.boolean(),
    selectableProviders: z.array(z.union([emailProviderNameSchema, z.literal('none')])),
  }),
})

export const adminEmailSettingsScreen = defineScreen({
  id: 'admin.email.settings',
  path: '/api/screens/admin/email/settings',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    provider: screenSection(adminPlatformEmailSettingsViewSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.email.settings'],
  budget: { requests: 1, statements: 8, bytes: 20_000, p95Ms: 250 },
  summary:
    'E-mail provider: the platform provider, what organizations without their own get, and who uses it',
})
export type AdminEmailSettingsData = ScreenData<typeof adminEmailSettingsScreen>
